import React, { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const CATEGORIES = ["Laptop", "Monitor", "Keyboard", "Mouse", "Phone", "Headset", "Tablet", "Docking Station", "Other"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];

const PRIORITY_CONFIG = {
  Low: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", dot: "#22c55e" },
  Medium: { color: "#d97706", bg: "#fffbeb", border: "#fde68a", dot: "#f59e0b" },
  High: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", dot: "#ef4444" },
  Critical: { color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe", dot: "#8b5cf6" },
};

const STATUS_CONFIG = {
  ACTIVE: { color: "#065f46", bg: "#ecfdf5", border: "#a7f3d0", label: "Active" },
  RETURNED: { color: "#991b1b", bg: "#fef2f2", border: "#fecaca", label: "Returned" },
  Pending: { color: "#92400e", bg: "#fffbeb", border: "#fde68a", label: "Pending" },
  Approved: { color: "#065f46", bg: "#ecfdf5", border: "#a7f3d0", label: "Approved" },
  Rejected: { color: "#991b1b", bg: "#fef2f2", border: "#fecaca", label: "Rejected" },
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; }

  html, body, #root {
    margin: 0; padding: 0; height: 100%;
  }

  .emp-assets-root {
    font-family: 'Sora', sans-serif;
    background: #f4f6fb;
    min-height: 100vh;
    height: 100%;
    color: #1a1f36;
    display: flex;
    flex-direction: column;
  }

  /* ── METRIC CARDS ───────────────────────── */
  .metric-card {
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 16px 22px;
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 170px;
    transition: box-shadow 0.2s, transform 0.2s;
  }
  .metric-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.08); transform: translateY(-2px); }
  .metric-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; flex-shrink: 0;
  }
  .metric-val { font-size: 22px; font-weight: 700; line-height: 1; }
  .metric-lbl { font-size: 11px; font-weight: 500; color: #8892a4; letter-spacing: 0.4px; margin-top: 3px; text-transform: uppercase; }

  /* ── TABS ───────────────────────────────── */
  .tab-bar {
    display: flex; gap: 4px;
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    padding: 5px;
    margin-bottom: 20px;
  }
  .tab-btn {
    flex: 1; display: flex; align-items: center; justify-content: center;
    gap: 8px; padding: 10px 18px;
    border: none; cursor: pointer; border-radius: 8px;
    font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 500;
    background: transparent; color: #8892a4;
    transition: all 0.18s ease;
  }
  .tab-btn:hover:not(.active) { background: #f4f6fb; color: #1a1f36; }
  .tab-btn.active {
    background: #080235; color: #fff;
    box-shadow: 0 2px 10px rgba(8,2,53,0.25);
  }
  .tab-btn .tab-icon { font-size: 15px; }

  /* ── TABLE ──────────────────────────────── */
  .asset-table { width: 100%; min-width: 700px; border-collapse: separate; border-spacing: 0; font-size: 13.5px; }
  .asset-table thead tr th {
    background: #f8f9fc; padding: 12px 16px;
    font-size: 10.5px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.7px; color: #8892a4; border-bottom: 1px solid #e8eaf0;
    white-space: nowrap;
  }
  .asset-table thead tr th:first-child { border-radius: 10px 0 0 0; }
  .asset-table thead tr th:last-child  { border-radius: 0 10px 0 0; }
  .asset-table tbody tr td {
    padding: 13px 16px; border-bottom: 1px solid #f0f2f7;
    vertical-align: middle; color: #2d3652;
  }
  .asset-table tbody tr:last-child td { border-bottom: none; }
  .asset-table tbody tr { transition: background 0.12s; }
  .asset-table tbody tr:hover td { background: #f8f9fc; }

  /* ── BADGES ─────────────────────────────── */
  .badge-status {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 600; border: 1px solid;
    white-space: nowrap;
  }
  .badge-status::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: currentColor; display: block; }
  .asset-code {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px; font-weight: 500;
    background: #eef0f8; color: #3d4c7a;
    padding: 3px 9px; border-radius: 6px;
    border: 1px solid #dde1f0;
  }
  .type-badge {
    background: #f4f6fb; color: #5a6482;
    padding: 3px 9px; border-radius: 6px;
    font-size: 11.5px; font-weight: 500;
    border: 1px solid #e2e5f0;
  }

  /* ── FORM ───────────────────────────────── */
  .form-section-title {
    font-size: 10.5px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 1px; color: #8892a4; margin-bottom: 12px;
    padding-bottom: 8px; border-bottom: 1px solid #f0f2f7;
  }
  .field-label {
    display: block; font-size: 11.5px; font-weight: 600;
    color: #4a5270; margin-bottom: 6px; letter-spacing: 0.2px;
  }
  .field-label .req { color: #ef4444; margin-left: 3px; }
  .form-input, .form-select, .form-textarea {
    width: 100%; border: 1.5px solid #e2e5f0; border-radius: 9px;
    padding: 10px 13px; font-family: 'Sora', sans-serif; font-size: 13.5px;
    color: #1a1f36; background: #fff; outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    -webkit-appearance: none; appearance: none;
  }
  .form-input::placeholder, .form-textarea::placeholder { color: #b4bdd0; }
  .form-input:focus, .form-select:focus, .form-textarea:focus {
    border-color: #080235; box-shadow: 0 0 0 3px rgba(8,2,53,0.08);
  }
  .form-textarea { resize: vertical; min-height: 90px; }
  .select-wrap { position: relative; }
  .select-wrap::after {
    content: '▾'; position: absolute; right: 13px; top: 50%;
    transform: translateY(-50%); pointer-events: none;
    color: #8892a4; font-size: 12px;
  }

  /* ── BUTTONS ────────────────────────────── */
  .btn-primary-custom {
    background: #080235; color: #fff; border: none;
    padding: 11px 24px; border-radius: 9px;
    font-family: 'Sora', sans-serif; font-size: 13.5px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
    transition: all 0.18s ease; letter-spacing: 0.2px;
  }
  .btn-primary-custom:hover:not(:disabled) { background: #130466; box-shadow: 0 4px 14px rgba(8,2,53,0.3); transform: translateY(-1px); }
  .btn-primary-custom:disabled { opacity: 0.55; cursor: not-allowed; }
  .btn-ghost {
    background: transparent; color: #5a6482; border: 1.5px solid #e2e5f0;
    padding: 10px 20px; border-radius: 9px;
    font-family: 'Sora', sans-serif; font-size: 13.5px; font-weight: 500;
    cursor: pointer; transition: all 0.15s;
  }
  .btn-ghost:hover { border-color: #c0c5d4; background: #f8f9fc; }

  /* ── INFO CARD (employee details) ────────── */
  .info-card {
    background: linear-gradient(135deg, #f8f9fc 0%, #eef0f8 100%);
    border: 1px solid #e2e5f0; border-radius: 14px; padding: 20px 24px;
    margin-bottom: 28px;
  }
  .info-field-label {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.8px; color: #a0a9bf; margin-bottom: 3px;
  }
  .info-field-val { font-size: 13.5px; font-weight: 600; color: #1a1f36; }

  /* ── EMPTY STATE ────────────────────────── */
  .empty-state {
    text-align: center; padding: 64px 32px; color: #a0a9bf;
  }
  .empty-icon {
    width: 64px; height: 64px; border-radius: 16px;
    background: #f4f6fb; display: inline-flex; align-items: center;
    justify-content: center; font-size: 28px; margin-bottom: 16px;
    border: 1.5px solid #e8eaf0;
  }
  .empty-title { font-size: 15px; font-weight: 600; color: #4a5270; margin: 0 0 6px; }
  .empty-sub   { font-size: 13px; color: #a0a9bf; margin: 0 0 20px; }

  /* ── ACKNOWLEDGED PILL ──────────────────── */
  .ack-yes { color: #16a34a; font-size: 12.5px; font-weight: 600; display: inline-flex; align-items: center; gap: 5px; }
  .ack-no  { color: #d97706; font-size: 12.5px; font-weight: 600; display: inline-flex; align-items: center; gap: 5px; }

  /* ── PANEL ──────────────────────────────── */
  .panel {
    background: #fff; border: 1px solid #e8eaf0;
    border-radius: 16px; overflow: hidden;
    width: 100%;
  }

  /* ── TABLE WRAPPER ──────────────────────── */
  .table-wrapper {
    width: 100%; overflow-x: auto;
  }

  /* ── SPINNER ────────────────────────────── */
  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner {
    width: 24px; height: 24px; border-radius: 50%;
    border: 2.5px solid #e8eaf0; border-top-color: #080235;
    animation: spin 0.7s linear infinite; margin: 60px auto;
  }

  /* ── ANIMATIONS ─────────────────────────── */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .animate-in { animation: fadeUp 0.28s ease both; }
`;

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { color: "#5a6482", bg: "#f4f6fb", border: "#e2e5f0", label: status };
  return (
    <span className="badge-status" style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>
      {cfg.label || status}
    </span>
  );
}

function PriorityBadge({ priority }) {
  if (!priority) return <span style={{ color: "#c0c5d4", fontSize: 12 }}>—</span>;
  const cfg = PRIORITY_CONFIG[priority] || {};
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600,
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
      {priority}
    </span>
  );
}

const initialForm = {
  assetCategory: "",
  justification: "", expectedDate: "", priority: "",
};

export default function EmpAssets() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const token      = localStorage.getItem("token");
  const employeeId = localStorage.getItem("employeeId");

  const [activeTab, setActiveTab] = useState(location.state?.tab || "allocations");
  const [employee, setEmployee] = useState(null);
  const [loadingEmp, setLoadingEmp] = useState(true);
  const [allocations, setAllocations] = useState([]);
  const [loadingAlloc, setLoadingAlloc] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [assetOptions, setAssetOptions] = useState([]);

  // Inject styles
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = styles;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  // Bootstrap icons only
  useEffect(() => {
    const ic = document.createElement("link");
    ic.rel = "stylesheet";
    ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(ic);
    return () => ic.remove();
  }, []);

  // Auth + employee data
  useEffect(() => {
    if (!token || !employeeId) { toast.error("Session expired. Please login."); navigate("/"); return; }
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/employees/${employeeId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error();
        setEmployee(await res.json());
      } catch { toast.error("Failed to load employee profile."); }
      finally { setLoadingEmp(false); }
    };
    load();
  }, [token, employeeId, navigate]);

  useEffect(() => {
  const fetchAssets = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/assets?page=0&size=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Assets status:", res.status); // 🔍 should be 200, if 403 = role issue
      const data = await res.json();
      console.log("Assets data:", data);          // 🔍 check content array
      setAssetOptions(Array.isArray(data) ? data : data.content || []);
    } catch (e) {
      console.error("Assets fetch error:", e);
      setAssetOptions([]);
    }
  };
  fetchAssets();
}, [token]);

  const fetchAllocations = useCallback(async () => {
    if (!employeeId) return;
    setLoadingAlloc(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-allocations/employee/${employeeId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAllocations(Array.isArray(data) ? data : data.content || []);
    } catch { setAllocations([]); }
    finally { setLoadingAlloc(false); }
  }, [token, employeeId]);

  const fetchRequests = useCallback(async () => {
    if (!employeeId) return;
    setLoadingReqs(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-requests/employee/${employeeId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch { setRequests([]); }
    finally { setLoadingReqs(false); }
  }, [token, employeeId]);

  useEffect(() => {
    if (activeTab === "allocations") fetchAllocations();
    if (activeTab === "requests") fetchRequests();
  }, [activeTab, fetchAllocations, fetchRequests]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.assetCategory || !formData.justification) {
      toast.error("Category and justification are required."); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-requests/${employeeId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to submit request.");
      toast.success("Request submitted successfully!");
      setFormData(initialForm);
      if (activeTab === "requests") fetchRequests();
    } catch (err) {
      toast.error(err.message || "Failed to submit request.");
    } finally { setSubmitting(false); }
  };

  const activeCount = allocations.filter(a => a.status === "ACTIVE").length;
  const pendingCount = requests.filter(r => (r.status || "").toLowerCase() === "pending").length;
  const approvedCount = requests.filter(r => (r.status || "").toLowerCase() === "approved").length;

  const TABS = [
    { key: "allocations", icon: "bi-laptop", label: "My Assets" },
    { key: "new-request", icon: "bi-plus-circle", label: " Asset Request" },
    { key: "requests", icon: "bi-clock-history", label: "My Requests" },
  ];

  if (loadingEmp) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#f4f6fb" }}>
      <div style={{ textAlign: "center" }}>
        <div className="spinner" />
        <p style={{ marginTop: 12, color: "#8892a4", fontFamily: "Sora, sans-serif", fontSize: 13 }}>Loading your profile…</p>
      </div>
    </div>
  );

  return (
    <div className="emp-assets-root">
      <ToastContainer position="top-right" autoClose={4000} />
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <AssetModuleNavbar />
      </div>

      <div style={{ flex: 1, padding: "24px 32px 40px", paddingTop: 130, width: "100%" }}>

        {/* ── PAGE HEADER ─────────────────────── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#080235", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="bi bi-laptop" style={{ color: "#fff", fontSize: 18 }} />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#080235", letterSpacing: "-0.3px" }}>Asset Portal</h1>
                {employee && (
                  <p style={{ margin: 0, fontSize: 12.5, color: "#8892a4", marginTop: 1 }}>
                    {employee.firstName} {employee.lastName}
                    <span style={{ margin: "0 6px", color: "#d0d4df" }}>·</span>
                    {employee.designation || "—"}
                    <span style={{ margin: "0 6px", color: "#d0d4df" }}>·</span>
                    {employee.department?.name || employee.department || "—"}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Metric cards */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[
              { icon: "bi-laptop-fill", val: activeCount, label: "Active Assets", iconBg: "#eef0f8", iconColor: "#3d4c7a" },
              { icon: "bi-hourglass-split", val: pendingCount, label: "Pending", iconBg: "#fffbeb", iconColor: "#d97706" },
              { icon: "bi-check-circle-fill", val: approvedCount, label: "Approved", iconBg: "#ecfdf5", iconColor: "#16a34a" },
            ].map(({ icon, val, label, iconBg, iconColor }) => (
              <div className="metric-card" key={label}>
                <div className="metric-icon" style={{ background: iconBg }}>
                  <i className={`bi ${icon}`} style={{ color: iconColor }} />
                </div>
                <div>
                  <div className="metric-val">{val}</div>
                  <div className="metric-lbl">{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── TABS ──────────────────────────────── */}
        <div className="tab-bar">
          {TABS.map(({ key, icon, label }) => (
            <button key={key} className={`tab-btn ${activeTab === key ? "active" : ""}`}
              onClick={() => setActiveTab(key)}>
              <i className={`bi ${icon} tab-icon`} />
              {label}
            </button>
          ))}
        </div>

        {/* ── MY ASSETS ────────────────────────── */}
        {activeTab === "allocations" && (
          <div className="panel animate-in">
            {loadingAlloc ? (
              <div className="spinner" />
            ) : allocations.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><i className="bi bi-inbox" /></div>
                <p className="empty-title">No assets allocated yet</p>
                <p className="empty-sub">Assets assigned to you will appear here.</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="asset-table">
                  <thead>
                    <tr>
                      <th>Asset Code</th>
                      <th>Asset Name</th>
                      <th>Type</th>
                      <th>Condition</th>
                      <th>Allocated On</th>
                      <th>Return By</th>
                      <th>Status</th>
                      <th>Acknowledged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocations.map(a => (
                      <tr key={a.id}>
                        <td><span className="asset-code">{a.assetCode || "—"}</span></td>
                        <td style={{ fontWeight: 600, color: "#1a1f36" }}>{a.assetName || "—"}</td>
                        <td><span className="type-badge">{a.allocationType || "—"}</span></td>
                        <td style={{ color: "#8892a4" }}>{a.conditionAtIssue || "—"}</td>
                        <td style={{ color: "#5a6482" }}>{a.allocationDate || "—"}</td>
                        <td style={{ color: a.expectedReturnDate ? "#d97706" : "#c0c5d4", fontWeight: a.expectedReturnDate ? 500 : 400 }}>
                          {a.expectedReturnDate || "Open-ended"}
                        </td>
                        <td><StatusBadge status={a.status} /></td>
                        <td>
                          {a.acknowledged
                            ? <span className="ack-yes"><i className="bi bi-check-circle-fill" />Yes</span>
                            : <span className="ack-no"><i className="bi bi-clock" />Pending</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── REQUEST ASSET ─────────────────────── */}
        {activeTab === "new-request" && (
          <div className="animate-in">
            {/* Employee info */}
            {employee && (
              <div className="info-card">
                <p className="form-section-title" style={{ marginBottom: 16 }}>Requesting Employee</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "14px 24px" }}>
                  {[
                    ["Full Name", `${employee.firstName} ${employee.lastName}`],
                    ["Employee ID", employee.employeeId || employee.id],
                    ["Department", employee.department?.name || employee.department || "—"],
                    ["Designation", employee.designation || "—"],
                    ["Manager", employee.reporter ? `${employee.reporter.firstName} ${employee.reporter.lastName}` : "—"],
                    ["Location", [employee.city, employee.state].filter(Boolean).join(", ") || "—"],
                  ].map(([lbl, val]) => (
                    <div key={lbl}>
                      <div className="info-field-label">{lbl}</div>
                      <div className="info-field-val">{val}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="panel" style={{ padding: 28 }}>
              <p className="form-section-title">Asset Request Details</p>
              <form onSubmit={handleSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 18 }}>

                  {/* Category */}
                  <div>
                    <label className="field-label">Asset <span className="req">*</span></label>
                    <div className="select-wrap">
                      <select
                        name="assetCategory"
                        className="form-select"
                        value={formData.assetCategory}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select asset…</option>
                        {assetOptions.map(a => (
                          <option key={a.id} value={a.assetName}>
                            {a.assetName} {a.assetCode ? `(${a.assetCode})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="field-label">Priority</label>
                    <div className="select-wrap">
                      <select name="priority" className="form-select" value={formData.priority} onChange={handleChange}>
                        <option value="">Select priority…</option>
                        {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Expected Date */}
                  <div>
                    <label className="field-label">Expected By Date</label>
                    <input type="date" name="expectedDate" className="form-input" value={formData.expectedDate} onChange={handleChange} />
                  </div>

                  {/* Justification — full width */}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label className="field-label">Justification <span className="req">*</span></label>
                    <textarea name="justification" className="form-textarea" value={formData.justification}
                      onChange={handleChange} placeholder="Provide a clear business reason for this request…" required />
                  </div>

                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
                  <button type="submit" className="btn-primary-custom" disabled={submitting}>
                    {submitting
                      ? <><span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Submitting…</>
                      : <><i className="bi bi-send" /> Submit Request</>}
                  </button>
                  <button type="button" className="btn-ghost" onClick={() => setFormData(initialForm)}>
                    Clear
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── MY REQUESTS ───────────────────────── */}
        {activeTab === "requests" && (
          <div className="panel animate-in">
            {loadingReqs ? (
              <div className="spinner" />
            ) : requests.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon"><i className="bi bi-file-earmark-text" /></div>
                <p className="empty-title">No requests yet</p>
                <p className="empty-sub">Your submitted asset requests will appear here.</p>
                <button className="btn-primary-custom" onClick={() => setActiveTab("new-request")}>
                  <i className="bi bi-plus-circle" /> Make a Request
                </button>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="asset-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>Sr.no</th>
                      <th>Asset</th>
                      <th>Priority</th>
                      <th>Expected By</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r, i) => (
                      <tr key={r.id}>
                        <td style={{ color: "#c0c5d4", fontSize: 12, fontFamily: "JetBrains Mono, monospace" }}>{String(i + 1).padStart(2, "0")}</td>
                        <td style={{ fontWeight: 600, color: "#1a1f36" }}>{r.assetCategory || "—"}</td>
                        <td><PriorityBadge priority={r.priority} /></td>
                        <td style={{ color: "#5a6482" }}>{r.expectedDate || "—"}</td>
                        <td><StatusBadge status={r.status || "Pending"} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>

    </div>

  );
}
